import { Component, ElementRef, ViewChild } from '@angular/core';
import {
  Validators,
  FormBuilder,
  FormArray,
  FormGroup,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { Router, ActivatedRoute } from '@angular/router';
import * as moment from 'moment';
import { Observable, startWith, map, combineLatest } from 'rxjs';
import { Country } from 'src/app/core/models/country.model';
import { QuotationInfo } from 'src/app/core/models/quotations-info.model';
import { Quote } from 'src/app/core/models/quote.model';
import { TravelPurpose } from 'src/app/core/models/travel-purpose.model';
import { Traveller } from 'src/app/core/models/traveller.model';
import { ContentLoaderService } from 'src/app/core/services/content-loader.service';
import { LoaderService } from 'src/app/core/services/loader.service';
import { RestService } from 'src/app/core/services/rest.service';
import { ErrorHandlerService } from 'src/app/core/services/error-handler.service';
import { TranslateService } from '@ngx-translate/core';
const FAMILY_CATEGORY_MASTER_ID = '2e891a8d-5f4c-4be7-8e07-3f212f82624a'; // Example ID for Family
const GROUP_CATEGORY_MASTER_ID = 'bc06d924-a3f7-4b6a-b380-43ea6d173aca';
const INBOUND_TRAVEL_TYPE_MASTER_ID = 'b7bc748b-70b8-4e58-bec3-986503917cda';
const INDIVIDUAL_CATEGORY_MASTER_ID = 'bc7b3c24-8d59-4ad1-8298-52bebf1e15ca';
const DOMESTIC_TRAVEL_TYPE_MASTER_ID = '0bb14cab-9bdf-4204-9d87-b44036ea7b63';
export function atLeastOneProposerValidator(): ValidatorFn {
  return (formArray: AbstractControl): ValidationErrors | null => {
    const isAtLeastOneSelected = (formArray as FormArray).controls.some(
      (control) => control.get('isProposerATraveller')?.value
    );

    return isAtLeastOneSelected ? null : { atLeastOneRequired: true };
  };
}
export function minAgeValidation(): ValidatorFn {
  return (formArray: AbstractControl): ValidationErrors | null => {
    const isAtLeastOneAboveOrEqual3 = (formArray as FormArray).controls.some(
      (control) => {
        const age = control.get('age')?.value;
        return age !== null && age >= 3;
      }
    );

    return isAtLeastOneAboveOrEqual3 ? null : { minAgeRequired: true };
  };
}
export function maxTripDurationValidator(maxDays: number): ValidatorFn {
  return (control: AbstractControl) => {
    const formGroup = control.parent; // Access the parent form group
    if (!formGroup) {
      return null; // Return if parent does not exist
    }

    const tripStartDate = formGroup.get('tripStartDate')?.value;
    const tripEndDate = control.value;

    if (tripStartDate && tripEndDate) {
      const startDate = new Date(tripStartDate);
      const endDate = new Date(tripEndDate);

      // Strip the time part from both dates
      const startDateOnly = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate()
      );
      const endDateOnly = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate()
      );

      const diffInTime = endDateOnly.getTime() - startDateOnly.getTime();
      const diffInDays = diffInTime / (1000 * 3600 * 24); // Convert milliseconds to days

      if (diffInDays > maxDays) {
        return { maxTripDurationExceeded: true }; // Return error if exceeds maxDays
      }
    }
    return null; // Return null if valid
  };
}
@Component({
  selector: 'app-get-quote',
  templateUrl: './get-quote.component.html',
  styleUrls: ['./get-quote.component.scss'],
})
export class GetQuoteComponent {
  familyCategoryMasterId: string = FAMILY_CATEGORY_MASTER_ID;
  private readonly groupCategoryMasterId: string = GROUP_CATEGORY_MASTER_ID;
  submitted: boolean = false;
  filteredDestinations: Observable<Country[]> | undefined;
  selectedDestinations: Country[] = [];
  @ViewChild('destinationInput')
  destinationInput!: ElementRef<HTMLInputElement>;
  minTripStartDate: any = new Date();
  minTripEndDate: any = new Date();
  quotationFieldsForm = this.fb.group({
    travelPurpose: ['', Validators.required],
    destination: [''],
    tripStartDate: ['', Validators.required],
    tripEndDate: ['', [Validators.required]],
    travelingDays: [{ value: '', disabled: true }, Validators.required],
    uploadType: ['', Validators.required],
    numberOfTravelers: [
      '',
      [Validators.required, this.maxTravelersValidator(10)],
    ],
    travelersDetails: this.fb.array([]),
    quotationTemplate: [''],
    familyTypeID: ['', Validators.required],
    travelTypeID: ['', Validators.required],
    countryOfOrigin: [''],
    isDaysTrip: [false],
  });
  dateOfBirthMaxDate = new Date();
  currentMessage!: string;
  formSubmitted: boolean = false;
  getTravelPurposeList: TravelPurpose[] = [];
  getCountriesList: Country[] = [];
  getTravelCategory: any[] = [];
  getTravelTypeList: any[] = [];
  schegencountries: any[] = [];
  durationTypes: any[] = [
    { value: false, name: 'Max 92 Days Trip' },
    { value: true, name: 'Max 180 Days Trip' },
  ];
  quotationId: string = '';
  isEditMode: boolean = false;
  quotesInfo!: QuotationInfo;
  quotesList: Quote[] = [];
  travellerList: Traveller[] = [];
  messages = [
    'QUOTATION.MESSAGES.INITIALIZING',
    'QUOTATION.MESSAGES.CHECKING_OPTIONS',
    'QUOTATION.MESSAGES.CALCULATING_PREMIUMS',
    'QUOTATION.MESSAGES.SECURING_RATES',
    'QUOTATION.MESSAGES.FINALIZING_DETAILS',
    'QUOTATION.MESSAGES.THANK_YOU',
  ];
  defaultCountry = [
    {
      FromCountryId: '771c2959-b859-47a5-89bb-b31f3f5dd07a',
      FromCountryName: 'KENYA',
      FromCountrySourceID: 101,
      FromCountryCode: null,
      ToCountryCode: 'KE',
      ToCountrySourceId: 101,
      TocountryID: '771c2959-b859-47a5-89bb-b31f3f5dd07a',
      ToCountryName: 'KENYA',
    },
  ];
  private flowTypeBaseConfig: any[] = [
    {
      id: '1',
      labelKey: 'QUOTATION.FLOW_TYPES.SINGLE_TRAVELLER_LABEL',
      value: 'single',
      descriptionKey: 'QUOTATION.FLOW_TYPES.SINGLE_TRAVELLER_DESC',
    },
    {
      id: '2',
      labelKey: 'QUOTATION.FLOW_TYPES.BULK_UPLOAD_LABEL',
      value: 'bulk',
      descriptionKey: 'QUOTATION.FLOW_TYPES.BULK_UPLOAD_DESC',
    },
  ];

  // This flowTypeList will be populated with translated strings and used by your template
  flowTypeList: any[] = [];
  parsedData: string | null = null;
  selectedFile: File | null = null;
  uploadedTemplateFile!: string;
  uploadedTemplateFileName!: string;
  currentLanguage: string = '';
  public showBulkUploadOption: boolean = true; // New property to control visibility
  public isBulkUploadOptionDisabled: boolean = false; // Existing property for disabling
  public showTripDurationField: boolean = false; // Property to control Trip Duration field visibility
  public readonly INBOUND_TRAVEL_TYPE_MASTER_ID =
    'b7bc748b-70b8-4e58-bec3-986503917cda';
  public readonly PILGRIMAGE_TRAVEL_PURPOSE_MASTER_ID =
    'c764a795-950a-4969-b84c-9fd161ac30ee';
  public readonly DOMESTIC_TRAVEL_TYPE_MASTER_ID =
    '0bb14cab-9bdf-4204-9d87-b44036ea7b63';
  public readonly TRAVEL_TRAVEL_TYPE_MASTER_ID =
    '7b881580-b5ed-44b7-8536-6bcb626c9ed3';
  constructor(
    private fb: FormBuilder,
    private restService: RestService,
    private loader: LoaderService,
    private router: Router,
    private contentLoaderService: ContentLoaderService,
    private route: ActivatedRoute,
    private errorHandler: ErrorHandlerService,
    private translate: TranslateService
  ) {
    this.route.params.subscribe((params) => {
      this.quotationId = params['quotationId'];
    });
    this.isEditMode = this.quotationId ? true : false;
    this.filteredDestinations = this.quotationFieldsForm
      .get('destination')
      ?.valueChanges.pipe(
        startWith(''),
        map((value) => this._filter(value || ''))
      ) as Observable<Country[]>;
    this.currentLanguage = this.translate.currentLang;
    this.translate.onLangChange.subscribe((event: any) => {
      this.currentLanguage = event.lang;
      this.GetMasterData();
      this._initializeAndTranslateFlowTypeList();
      console.log(
        'Language changed to in GetQuoteComponent:',
        this.currentLanguage
      );
      // You might want to re-fetch or re-process data if it's language-dependent
      // this.GetMasterData(); // Example: Re-fetch master data if it depends on language
    });
  }
  maxAgeValidator(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = parseInt(control.value, 10);
      if (!isNaN(value) && value > max) {
        return { maxAge: true };
      }
      return null;
    };
  }
  maxTravelersValidator(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      console.log('maxTravelersValidator called with value:', control.value);
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null; // Let required validator handle empty
      }
      const num = Number(value);
      if (isNaN(num) || num > max) {
        return { maxTravelers: true };
      }
      return null;
    };
  }

  ngOnInit() {
    this._initializeAndTranslateFlowTypeList();
    this.GetMasterData();
    this._setupTravelTypeSubscription(); // Call the new setup method
    this._setupTripDurationVisibilitySubscription(); // Setup Trip Duration visibility
    this.quotationFieldsForm
      .get('uploadType')
      ?.valueChanges.subscribe((val: any) => {
        this.selectedFile = null;
        if (val == '1') {
          // Manual Entry
          // When switching to Manual Entry, ensure numberOfTravelers has a validator if it's not disabled (e.g. by Individual category)
          if (!this.quotationFieldsForm.get('numberOfTravelers')?.disabled) {
            this.updateFormControl(
              'numberOfTravelers',
              this.quotationFieldsForm.get('numberOfTravelers')?.value || '',
              [Validators.required, this.maxTravelersValidator(10)]
            );
          }
          this.getTravelersDetails.clear(); // Clear existing travelers
          // Add default traveler if numberOfTravelers is 1 (common for manual single entry)
          if (this.quotationFieldsForm.get('numberOfTravelers')?.value == '1') {
            this.numberOfTravelers(); // This will add one traveler detail form
          }
          this.updateFormControl(
            'travelersDetails',
            this.getTravelersDetails.value,
            [atLeastOneProposerValidator(), minAgeValidation()]
          );
          this.updateFormControl('quotationTemplate', '', []);
        } else if (val == '2') {
          this.updateFormControl('quotationTemplate', '', [
            Validators.required,
          ]);
          this.updateFormControl('numberOfTravelers', '', []);
          this.getTravelersDetails.clear();
          this.updateFormControl('travelersDetails', [], []);
        }
      });
    // const familyTypeControl = this.quotationFieldsForm.get('familyTypeID');
    // if (familyTypeControl) {
    //   // Use startWith to run the logic for the initial value as well
    //   familyTypeControl.valueChanges.pipe(startWith(familyTypeControl.value)).subscribe(() => {
    //     this.updateBulkUploadSettings();
    //   });
    // }
    const familyTypeControl = this.quotationFieldsForm.get('familyTypeID');
    if (familyTypeControl) {
      familyTypeControl.valueChanges
        .pipe(startWith(familyTypeControl.value))
        .subscribe((selectedFamilyTypeID) => {
          const uploadTypeControl = this.quotationFieldsForm.get('uploadType');
          const numberOfTravelersControl =
            this.quotationFieldsForm.get('numberOfTravelers');

          if (selectedFamilyTypeID === INDIVIDUAL_CATEGORY_MASTER_ID) {
            // Set to Manual Entry ('1') and disable
            uploadTypeControl?.patchValue('1');
            uploadTypeControl?.disable();

            // Set to 1 traveler and disable
            numberOfTravelersControl?.setValue('1');
            numberOfTravelersControl?.disable();
            numberOfTravelersControl?.setValidators([
              Validators.required,
              this.maxTravelersValidator(10),
            ]);
            numberOfTravelersControl?.updateValueAndValidity();
            this.numberOfTravelers(); // This will clear and add 1 traveler detail

            this.showBulkUploadOption = false; // Hide bulk upload option
            this.isBulkUploadOptionDisabled = true; // Ensure it's marked as disabled conceptually
          } else {
            if (uploadTypeControl?.disabled) {
              uploadTypeControl?.enable();
              uploadTypeControl?.patchValue(''); // Reset to Manual Entry
            }
            // if (numberOfTravelersControl?.disabled) {
            numberOfTravelersControl?.enable();
            numberOfTravelersControl?.setValidators([
              Validators.required,
              this.maxTravelersValidator(10),
            ]);
            numberOfTravelersControl?.updateValueAndValidity();
            // }
            this.updateBulkUploadSettings(); // Handles bulk upload visibility for Family/Group etc.
          }
          uploadTypeControl?.updateValueAndValidity();
          numberOfTravelersControl?.updateValueAndValidity();
        });
    }

    this.quotationFieldsForm
      .get('travelPurpose')
      ?.valueChanges.subscribe((val) => {
        this.selectedDestinations = [];
        this.quotationFieldsForm.get('travelTypeID')?.patchValue(null);
        this.quotationFieldsForm.get('countryOfOrigin')?.patchValue(null);
      });
    this.quotationFieldsForm
      .get('travelTypeID')
      ?.valueChanges.subscribe((val) => {
        this.quotationFieldsForm.get('countryOfOrigin')?.patchValue(null);
      });
    this.quotationFieldsForm
      .get('tripStartDate')
      ?.valueChanges.subscribe((val) => {
        this.minTripEndDate = val;
        this.quotationFieldsForm.get('tripEndDate')?.patchValue(null);
        this.quotationFieldsForm.get('travelingDays')?.patchValue(null);
      });
    const startDateControl = this.quotationFieldsForm.get('tripStartDate');
    const endDateControl = this.quotationFieldsForm.get('tripEndDate');

    if (startDateControl && endDateControl) {
      const startDate$ = startDateControl.valueChanges;
      const endDate$ = endDateControl.valueChanges;

      combineLatest([startDate$, endDate$])
        .pipe(
          map(([start, end]) => {
            if (start && end) {
              // Only proceed if both dates have values
              const startDate = new Date(start);
              const endDate = new Date(end);

              if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
                // Calculate the difference in days, adding 1 to include both the start and end dates
                return (
                  Math.abs(
                    (endDate.getTime() - startDate.getTime()) /
                      (1000 * 3600 * 24)
                  ) + 1
                );
              }
            }
            return 0; // Return 0 if either date is missing or invalid
          })
        )
        .subscribe((days: any) => {
          // Set the travelingDays only if days is greater than 0 to avoid setting it for invalid dates
          if (days > 0) {
            this.quotationFieldsForm
              .get('travelingDays')
              ?.patchValue(days, { emitEvent: false });
          }
        });
    }
  }

  GetQuotesInfo() {
    let params = {
      QuotationID: this.quotationId,
    };
    this.loader.show();
    this.restService.postData(params, 'api/Quotation/GetQuotesInfo').subscribe({
      next: (data: any) => {
        try {
          if (data.rcode == 200) {
            this.quotesInfo = data.rObj.GetQuotesInfo;
            this.travellerList = this.quotesInfo.IsBulk
              ? []
              : data.rObj.Traveller || [];
            this.travellerList = data.rObj.Traveller || [];
            let selectedDestinationsList = data.rObj.DestinationCountry || [];
            let selectedDestinationsIds = selectedDestinationsList.map(
              (sdl: any) => sdl.DestinationCountryID
            );

            if (this.isEditMode && this.quotesInfo.IsBulk) {
              this.DownloadQuotationExcelFile();
            }

            if (this.isEditMode) {
              this.SetFormFieldsValuesOnEditMode();
              this.selectedDestinations = this.getCountriesList.filter(
                (country) =>
                  selectedDestinationsIds.includes(country.TocountryID)
              );
            }
          } else {
            this.errorHandler.handleRcodeError(data);
          }
        } catch (error) {
          this.errorHandler.internalError();
        } finally {
          this.loader.hide();
        }
      },
      error: (error: any) => {
        this.loader.hide();
        this.errorHandler.handleStatusError(error);
      },
    });
  }

  SetFormFieldsValuesOnEditMode() {
    this.quotationFieldsForm.patchValue({
      travelPurpose: this.quotesInfo.TravelPurposeID,
      tripStartDate: this.quotesInfo.FromDate,
      tripEndDate: this.quotesInfo.ToDate,
      travelingDays: this.quotesInfo.NoofDays?.toString(),
      uploadType: this.quotesInfo.IsBulk ? '2' : '1',
      numberOfTravelers: this.quotesInfo.IsBulk
        ? ''
        : this.quotesInfo.NoofTravellers?.toString(),
      familyTypeID: this.quotesInfo?.familyTypeID,
      travelTypeID: this.quotesInfo?.travelTypeID,
    });
    if (this.isEditMode) {
      // this.quotationFieldsForm.get('uploadType')?.disable();
    }
    this.getTravelersDetails.clear();
    for (const traveller of this.travellerList) {
      this.getTravelersDetails.push(
        this.newTravelersDetaileWithDefaultValues(traveller)
      );
    }
    this.quotationFieldsForm.get('familyTypeID')?.disable();
  }

  get getTravelersDetails(): FormArray {
    return this.quotationFieldsForm.get('travelersDetails') as FormArray;
  }

  newTravelersDetaile(numberOfTravelers: number): FormGroup {
    return this.fb.group({
      isProposerATraveller: [
        numberOfTravelers == 1 ? true : false,
        Validators.required,
      ],
      dateOfBirth: ['', Validators.required],
      age: ['', [Validators.required, this.maxAgeValidator(80)]],
    });
  }

  // newTravelersDetaileWithDefaultValues(traveller: Traveller): FormGroup {
  //   return this.fb.group({
  //     isProposerATraveller: [traveller.IsPolicyHolder, Validators.required],
  //     dateOfBirth: [traveller.DOB, Validators.required],
  //     age: [{ value: traveller.Age, disabled: true }, [Validators.required, this.maxAgeValidator(80)]]
  //   });
  // }
  newTravelersDetaileWithDefaultValues(traveller: Traveller): FormGroup {
    return this.fb.group({
      isProposerATraveller: [traveller.IsPolicyHolder, Validators.required],
      dateOfBirth: [traveller.DOB, Validators.required],
      age: [traveller.Age, [Validators.required, this.maxAgeValidator(80)]],
    });
  }
  onProposerChange(selectedGroup: any, selected: boolean) {
    if (selected) {
      this.disableOtherProposerControls(selectedGroup);
    } else {
      this.enableAllProposerControls();
    }
    this.getTravelersDetails.updateValueAndValidity();
  }

  disableOtherProposerControls(selectedGroup: FormGroup) {
    this.getTravelersDetails.controls.forEach((group) => {
      if (group !== selectedGroup) {
        group.get('isProposerATraveller')?.disable();
      }
    });
  }

  enableAllProposerControls() {
    this.getTravelersDetails.controls.forEach((group) => {
      group.get('isProposerATraveller')?.enable();
    });
  }

  removeTravelersDetaile(i: number) {
    this.getTravelersDetails.removeAt(i);
  }

  remove(destination: Country): void {
    const index = this.selectedDestinations.indexOf(destination);
    if (index >= 0) {
      this.selectedDestinations.splice(index, 1);
      this.onFocus();
      this.updateAgeValidatorsForTravelers(); // <--- Add this
    }
  }

  //
  selected(event: MatAutocompleteSelectedEvent): void {
    const selectedCountry = this.getCountriesList.find(
      (country) => country.TocountryID === event.option.value
    );
    if (selectedCountry) {
      let selectedTravelPurpose =
        this.quotationFieldsForm.get('travelPurpose')?.value;
      if (selectedTravelPurpose === '9cc927ea-0447-4f83-bb46-699da28c428e') {
        this.selectedDestinations = [];
      }
      this.selectedDestinations.push(selectedCountry);

      this.destinationInput.nativeElement.value = '';
      this.quotationFieldsForm.get('destination')?.patchValue('');
      this.onFocus();

      // Update age validators for travelers based on selected destinations
      this.updateAgeValidatorsForTravelers();
    }
  }

  // Add this helper method to your component if not already present:
  private updateAgeValidatorsForTravelers(): void {
    const schengenIds = new Set(
      (this.schegencountries || []).map((c: any) => c.ToCountryId)
    );
    const isExempt = this.selectedDestinations.some((dest) =>
      schengenIds.has(dest.TocountryID)
    );
    this.getTravelersDetails.controls.forEach((travelerGroup) => {
      const ageControl = travelerGroup.get('age');
      if (ageControl) {
        if (isExempt) {
          ageControl.setValidators([Validators.required]);
        } else {
          ageControl.setValidators([
            Validators.required,
            this.maxAgeValidator(80),
          ]);
        }
        ageControl.updateValueAndValidity();
      }
    });
  }

  private _filter(value: string): Country[] {
    const filterValue = value.toLowerCase();
    return this.getCountriesList.filter(
      (country) =>
        country.ToCountryName.toLowerCase().includes(filterValue) &&
        !this.selectedDestinations.some(
          (selected) => selected.TocountryID === country.TocountryID
        )
    );
  }

  onFocus() {
    this.filteredDestinations = this.quotationFieldsForm
      .get('destination')
      ?.valueChanges.pipe(
        startWith(''),
        map((value) => this._filter(value || ''))
      ) as Observable<Country[]>;
  }

  // numberOfTravelers(): void {
  //   this.getTravelersDetails.clear();
  //   const numberOfTravelers: any = this.quotationFieldsForm.get('numberOfTravelers')?.value || 0;
  //   const validNumber = parseInt(numberOfTravelers, 10);

  //   if (!isNaN(validNumber) && validNumber > 0) {
  //     for (let i = 1; i <= validNumber; i++) {
  //       this.getTravelersDetails.push(this.newTravelersDetaile(numberOfTravelers));
  //     }
  //   }
  // }
  numberOfTravelers(): void {
    const numberOfTravelersControl =
      this.quotationFieldsForm.get('numberOfTravelers');
    const numberOfTravelers: any = numberOfTravelersControl?.value || 0;
    const validNumber = parseInt(numberOfTravelers, 10);

    // Always update validity first
    numberOfTravelersControl?.updateValueAndValidity();

    // Only proceed if valid
    if (
      !numberOfTravelersControl?.invalid &&
      !isNaN(validNumber) &&
      validNumber > 0
    ) {
      this.getTravelersDetails.clear();
      for (let i = 1; i <= validNumber; i++) {
        this.getTravelersDetails.push(this.newTravelersDetaile(validNumber));
      }
      this.updateAgeValidatorsForTravelers(); // <--- Add this
    } else {
      // If invalid, do not clear or repopulate the array!
      // Optionally, you can clear the array here if you want no travelers shown when invalid:
      this.getTravelersDetails.clear();
    }
  }
  numberOnly(event: KeyboardEvent): boolean {
    // Allow: backspace, delete, tab, escape, enter, arrows
    if (
      [8, 9, 27, 13, 37, 38, 39, 40].indexOf(event.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (event.ctrlKey &&
        ['a', 'c', 'v', 'x'].indexOf(event.key.toLowerCase()) !== -1)
    ) {
      return true;
    }
    // Block if not a digit
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  calculateAge(dob: Date): number {
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  dateOfBirthChange(event: MatDatepickerInputEvent<Date>, index: number) {
    let dob = this.getTravelersDetails.at(index).get('dateOfBirth')?.value;
    let getAge = this.calculateAge(dob);
    this.getTravelersDetails.at(index).get('age')?.patchValue(getAge);
  }

  GetMasterData() {
    let params = {
      id: this.currentLanguage == 'en' ? '1' : '2', // Assuming 1 is for English and 2 for other languages
    };
    this.loader.show();
    this.restService.postData(params, 'api/Quotation/GetMasterData').subscribe({
      next: (data: any) => {
        try {
          if (data.rcode == 200) {
            this.getTravelPurposeList = data.rObj.GetTravelPurpose || [];
            this.getCountriesList = data.rObj.GetCountries || [];
            this.getTravelCategory = data.rObj.family || [];
            this.getTravelTypeList = data.rObj.travelType || [];
            this.schegencountries = data.rObj.schegencountries || [];
            console.log(this.schegencountries);
            if (this.isEditMode) {
              this.GetQuotesInfo();
            }
          } else {
            this.errorHandler.handleRcodeError(data);
          }
        } catch (error) {
          this.errorHandler.internalError();
        } finally {
          this.loader.hide();
        }
      },
      error: (error: any) => {
        this.loader.hide();
        this.errorHandler.handleStatusError(error);
      },
    });
  }

  submit() {
    this.submitted = true;
    if (this.quotationFieldsForm.invalid) {
      console.log(this.quotationFieldsForm.get('numberOfTravelers')?.errors);
      return;
    }
    if (
      this.quotationFieldsForm.invalid ||
      this.selectedDestinations.length == 0
    ) {
      return;
    }

    let travelersDetails =
      this.quotationFieldsForm.get('travelersDetails')?.getRawValue() || [];
    let params = {
      FromCountryID: '771C2959-B859-47A5-89BB-B31F3F5DD07A',
      TravelPurposeID: this.quotationFieldsForm.get('travelPurpose')?.value,
      ToCountriesId: this.selectedDestinations.map(
        ({ ToCountrySourceId, TocountryID, ToCountryName, ToCountryCode }) => ({
          CountrySourceId: ToCountrySourceId,
          CountryId: TocountryID,
          CountryName: ToCountryName,
          CountryCode: ToCountryCode,
        })
      ),
      FromDate: moment(
        this.quotationFieldsForm.get('tripStartDate')?.value
      ).format('YYYY-MM-DD'),
      ToDate: moment(this.quotationFieldsForm.get('tripEndDate')?.value).format(
        'YYYY-MM-DD'
      ),
      NoofDays: parseInt(
        this.quotationFieldsForm.get('travelingDays')?.value || '',
        10
      ),
      NoofTravellers:
        parseInt(
          this.quotationFieldsForm.get('numberOfTravelers')?.value || '',
          10
        ) || null,
      // NoofTravellers: this.quotationFieldsForm.get("numberOfTravelers")?.value?parseInt(this.quotationFieldsForm.get("numberOfTravelers")?.value || '', 10):null,
      TravellersInfo: travelersDetails.map((td: any) => {
        return {
          DateofBirth: moment(td.dateOfBirth).format('YYYY-MM-DD'),
          Age: parseInt(td.age, 10),
          IsPolicyHolder: td.isProposerATraveller,
        };
      }),
      isDaysTrip: this.quotationFieldsForm.get('isDaysTrip')?.value,
      travelTypeID: this.quotationFieldsForm.get('travelTypeID')?.value,
      familyTypeID: this.quotationFieldsForm.get('familyTypeID')?.value
        ? this.quotationFieldsForm.get('familyTypeID')?.value
        : null,
    };
    this.formSubmitted = true;
    if (this.quotationFieldsForm.get('uploadType')?.value == '1') {
      this.GetQuotes(params);
    } else if (this.quotationFieldsForm.get('uploadType')?.value == '2') {
      this.GetQuotesBulk(params);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];

      // this.onUpload(index);
    } else {
      this.quotationFieldsForm.get('quotationTemplate')?.patchValue('');
      this.selectedFile = null;
    }
  }

  private updateFormControl(
    controlName: string,
    value: any,
    validators: any[]
  ): void {
    const control = this.quotationFieldsForm.get(controlName);
    if (control) {
      control.patchValue(value);
      control.setValidators(validators);
      control.updateValueAndValidity();
    }
  }

  DownloadTemplate() {
    this.loader.show();
    this.restService
      .postData(null, 'api/Quotation/DownloadTemplate')
      .subscribe({
        next: (data: any) => {
          try {
            if (data.rcode == 1) {
              if (data.rObj.quotationPDFURL) {
                window.open(data.rObj.quotationPDFURL, '_target');
              }
            } else {
              this.errorHandler.handleRcodeError(data);
            }
          } catch (error) {
            this.errorHandler.internalError();
          } finally {
            this.loader.hide();
          }
        },
        error: (error: any) => {
          this.loader.hide();
          this.errorHandler.handleStatusError(error);
        },
      });
  }

  GetQuotes(params: any) {
    this.contentLoaderService
      .submitData('api/Quotation/GetQuotes', params, this.messages)
      .subscribe({
        next: (data: any) => {
          try {
            if (typeof data === 'string') {
              this.currentMessage = this.translate.instant(data);
            } else if (data.rcode == 200) {
              this.formSubmitted = false;
              this.router.navigate([
                'quotation/quotation-premium',
                data.rObj.QuotationID,
              ]);
            } else {
              this.errorHandler.handleRcodeError(data);
            }
          } catch (error) {
            this.errorHandler.internalError();
          }
        },
        error: (error) => {
          this.errorHandler.handleStatusError(error);
        },
      });
  }

  async GetQuotesBulk(params: any) {
    let stringParams = JSON.stringify(params);
    if (!this.selectedFile) {
      console.error('No file selected');
      return;
    }
    const formData = new FormData();
    formData.append('file', this.selectedFile, this.selectedFile.name);
    formData.append('sObject', stringParams);
    this.contentLoaderService
      .submitData('api/Quotation/GetQuotesBulk', formData, this.messages)
      .subscribe({
        next: (data: any) => {
          try {
            if (typeof data === 'string') {
              this.currentMessage = this.translate.instant(data);
            } else if (data.rcode == 200) {
              this.formSubmitted = false;
              this.router.navigate([
                'quotation/quotation-premium',
                data.rObj.QuotationID,
              ]);
            } else {
              this.errorHandler.handleRcodeError(data);
            }
          } catch (error) {
            this.errorHandler.internalError();
          }
        },
        error: (error) => {
          this.errorHandler.handleStatusError(error);
        },
      });
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.quotationFieldsForm
        .get('quotationTemplate')
        ?.patchValue(this.selectedFile.name);
    } else {
      this.quotationFieldsForm.get('quotationTemplate')?.patchValue('');
      this.selectedFile = null;
    }
  }

  triggerFileInput(): void {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    fileInput.click();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    const uploadBox = event.target as HTMLElement;
    uploadBox.classList.add('drag-over');
  }

  onDragLeave(event: DragEvent): void {
    const uploadBox = event.target as HTMLElement;
    uploadBox.classList.remove('drag-over');
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    const uploadBox = event.target as HTMLElement;
    uploadBox.classList.remove('drag-over');
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.selectedFile = event.dataTransfer.files[0];
      this.quotationFieldsForm
        .get('quotationTemplate')
        ?.patchValue(this.selectedFile?.name);
    } else {
      this.quotationFieldsForm.get('quotationTemplate')?.patchValue('');
      this.selectedFile = null;
    }
  }

  DownloadQuotationExcelFile() {
    let params = {
      QuotationId: this.quotationId,
    };
    this.restService
      .postData(params, 'api/Quotation/DownloadQuotationExcelFile')
      .subscribe({
        next: (data: any) => {
          try {
            if (data.rcode == 200) {
              this.uploadedTemplateFile = data.rObj.URL;
              this.uploadedTemplateFileName = this.getFileNameFromBlobUrl(
                this.uploadedTemplateFile
              );
            } else {
              this.errorHandler.handleRcodeError(data);
            }
          } catch (error) {
            this.errorHandler.internalError();
          }
        },
        error: (error) => {
          this.errorHandler.handleStatusError(error);
        },
      });
  }
  getFileNameFromBlobUrl(blobUrl: string): string {
    const urlPath = blobUrl.split('?')[0];
    let fileName = urlPath.substring(urlPath.lastIndexOf('/') + 1);
    const extensionIndex = fileName.lastIndexOf('.');
    const cleanFileName = fileName.slice(0, extensionIndex + 4);
    return decodeURIComponent(cleanFileName);
  }
  updateBulkUploadSettings(): void {
    const familyTypeControl = this.quotationFieldsForm.get('familyTypeID');

    // Default to showing the bulk upload option.
    // It will be hidden if a category is selected and it's NOT "Family" or "Group".
    let shouldShowBulk = true;

    if (familyTypeControl && familyTypeControl.value) {
      // This block executes if a travel category is actually selected.
      const selectedFamilyTypeID = familyTypeControl.value;

      // Hide the bulk upload option if the selected category is neither Family nor Group.
      if (
        selectedFamilyTypeID !== this.familyCategoryMasterId &&
        selectedFamilyTypeID !== this.groupCategoryMasterId
      ) {
        shouldShowBulk = false;
      }
    }

    this.showBulkUploadOption = shouldShowBulk;
    this.isBulkUploadOptionDisabled = false;

    this.resetUploadTypeIfDisabledOrHidden();
  }

  resetUploadTypeIfDisabledOrHidden(): void {
    const uploadTypeControl = this.quotationFieldsForm.get('uploadType');
    // Assuming '2' is the ID for "Bulk Upload"
    if (uploadTypeControl && uploadTypeControl.value === '2') {
      if (!this.showBulkUploadOption || this.isBulkUploadOptionDisabled) {
        uploadTypeControl.setValue(null); // Or set to '1' (Manual Entry) if that's preferred
        // uploadTypeControl.markAsDirty();
        // uploadTypeControl.updateValueAndValidity();
      }
    }
  }
  private _initializeAndTranslateFlowTypeList(): void {
    this.flowTypeList = this.flowTypeBaseConfig.map((item) => {
      return {
        id: item.id,
        label: this.translate.instant(item.labelKey), // Translate label
        value: item.value,
        description: this.translate.instant(item.descriptionKey), // Translate description
      };
    });
  }
  private _setupTravelTypeSubscription(): void {
    this.quotationFieldsForm
      .get('travelTypeID')
      ?.valueChanges.pipe(
        startWith(this.quotationFieldsForm.get('travelTypeID')?.value) // Process initial value (e.g., from edit mode)
      )
      .subscribe((selectedTravelTypeId) => {
        this._handleTravelTypeChange(selectedTravelTypeId);
      });
  }

  // private _handleTravelTypeChange(
  //   selectedTravelTypeId: string | null | undefined
  // ): void {
  //   const destinationControl = this.quotationFieldsForm.get('destination');
  //   const countryOfOriginControl =
  //     this.quotationFieldsForm.get('countryOfOrigin'); // Control for mat-select

  //   const isCurrentlyInbound =
  //     selectedTravelTypeId === this.INBOUND_TRAVEL_TYPE_MASTER_ID;
  //   const isCurrentlyDomestic =
  //     selectedTravelTypeId === DOMESTIC_TRAVEL_TYPE_MASTER_ID;
  //   if (isCurrentlyInbound || isCurrentlyDomestic) {
  //     // Handle Destination field (set to Kenya, disable)
  //     this.selectedDestinations = [...this.defaultCountry];
  //     destinationControl?.setValue(null);
  //     destinationControl?.disable();
  //     if (this.destinationInput?.nativeElement) {
  //       this.destinationInput.nativeElement.value = '';
  //     }

  //     // Handle Country of Origin (mat-select): Enable and make required
  //     countryOfOriginControl?.setValidators([Validators.required]);
  //     countryOfOriginControl?.enable();
  //   } else {
  //     // Not Inbound or switching away from Inbound
  //     // Handle Destination field (enable, clear if necessary)
  //     destinationControl?.enable();
  //     const isKenyaSelectedAsDefault =
  //       this.selectedDestinations.length === 1 &&
  //       this.selectedDestinations[0].TocountryID ===
  //         this.defaultCountry[0].TocountryID;

  //     if (isKenyaSelectedAsDefault) {
  //       let shouldClearKenya = false;
  //       if (!this.isEditMode) {
  //         shouldClearKenya = true;
  //       } else if (
  //         this.quotesInfo &&
  //         this.quotesInfo.travelTypeID === this.INBOUND_TRAVEL_TYPE_MASTER_ID
  //       ) {
  //         shouldClearKenya = true;
  //       }
  //       if (shouldClearKenya) {
  //         this.selectedDestinations = [];
  //       }
  //     }

  //     // Handle Country of Origin (mat-select): Clear value, validators, and disable
  //     countryOfOriginControl?.setValue(null); // Clear its value
  //     countryOfOriginControl?.clearValidators();
  //     countryOfOriginControl?.disable();
  //   }

  //   // Update validity for all affected controls
  //   destinationControl?.updateValueAndValidity();
  //   countryOfOriginControl?.updateValueAndValidity(); // Crucial for validation changes to take effect

  //   this.onFocus(); // Assuming this method exists and is relevant for the destination field
  // }
  private _handleTravelTypeChange(
    selectedTravelTypeId: string | null | undefined
  ): void {
    const destinationControl = this.quotationFieldsForm.get('destination');
    const countryOfOriginControl =
      this.quotationFieldsForm.get('countryOfOrigin');

    // Check if type is Inbound or Domestic
    const isCurrentlyInbound =
      selectedTravelTypeId === this.INBOUND_TRAVEL_TYPE_MASTER_ID;
    const isCurrentlyDomestic =
      selectedTravelTypeId === DOMESTIC_TRAVEL_TYPE_MASTER_ID;

    // Apply Kenya as default for both Inbound and Domestic
    if (isCurrentlyInbound || isCurrentlyDomestic) {
      // Set Kenya as destination and disable field
      this.selectedDestinations = [...this.defaultCountry];
      destinationControl?.setValue(null);
      destinationControl?.disable();
      if (this.destinationInput?.nativeElement) {
        this.destinationInput.nativeElement.value = '';
      }

      // Only enable Country of Origin for Inbound (not for Domestic)
      if (isCurrentlyInbound) {
        countryOfOriginControl?.setValidators([Validators.required]);
        countryOfOriginControl?.enable();
      } else {
        countryOfOriginControl?.setValue(null);
        countryOfOriginControl?.clearValidators();
        countryOfOriginControl?.disable();
      }
    } else {
      // Not Inbound or Domestic
      destinationControl?.enable();

      // Check if Kenya is selected as default and should be cleared
      const isKenyaSelectedAsDefault =
        this.selectedDestinations.length === 1 &&
        this.selectedDestinations[0].TocountryID ===
          this.defaultCountry[0].TocountryID;

      if (isKenyaSelectedAsDefault) {
        let shouldClearKenya = false;
        if (!this.isEditMode) {
          shouldClearKenya = true;
        } else if (
          this.quotesInfo &&
          (this.quotesInfo.travelTypeID ===
            this.INBOUND_TRAVEL_TYPE_MASTER_ID ||
            this.quotesInfo.travelTypeID === DOMESTIC_TRAVEL_TYPE_MASTER_ID)
        ) {
          shouldClearKenya = true;
        }
        if (shouldClearKenya) {
          this.selectedDestinations = [];
        }
      }

      // Handle Country of Origin for non-Inbound types
      countryOfOriginControl?.setValue(null);
      countryOfOriginControl?.clearValidators();
      countryOfOriginControl?.disable();
    }

    // Update validity
    destinationControl?.updateValueAndValidity();
    countryOfOriginControl?.updateValueAndValidity();
    this.onFocus();
  }

private _setupTripDurationVisibilitySubscription(): void {
  // Get the form control
  const travelTypeControl = this.quotationFieldsForm.get('travelTypeID');

  // Initial check
  this._updateTripDurationVisibility(travelTypeControl?.value || null);

  // Subscribe to travelTypeID changes
  travelTypeControl?.valueChanges.subscribe((travelTypeId) => {
    this._updateTripDurationVisibility(travelTypeId);
  });
}

private _updateTripDurationVisibility(
  travelTypeId: string | null | undefined
): void {
  // Show Trip Duration field if:
  // 1. Travel Type is Travel (7b881580-b5ed-44b7-8536-6bcb626c9ed3)
  // 2. Travel Type is Inbound (b7bc748b-70b8-4e58-bec3-986503917cda)
  // 3. Travel Type is Pilgrimage (c764a795-950a-4969-b84c-9fd161ac30ee)
  const shouldShow =
    travelTypeId === this.TRAVEL_TRAVEL_TYPE_MASTER_ID ||
    travelTypeId === this.INBOUND_TRAVEL_TYPE_MASTER_ID ||
    travelTypeId === this.PILGRIMAGE_TRAVEL_PURPOSE_MASTER_ID;
  
  this.showTripDurationField = shouldShow;
  
  // Update validators based on visibility
  const isDaysTripControl = this.quotationFieldsForm.get('isDaysTrip');
  if (shouldShow) {
    isDaysTripControl?.setValidators([Validators.required]);
  } else {
    isDaysTripControl?.clearValidators();
    isDaysTripControl?.setValue(false); // Reset to default value
  }
  isDaysTripControl?.updateValueAndValidity();
}
}
